import React, { useState, useRef, createContext, useContext, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Expand } from 'lucide-react';
import ScreenshotLightbox from './ScreenshotLightbox';

const GalleryContext = createContext(null);

export function ScreenshotGallery({ items, children }) {
  const [openIndex, setOpenIndex] = useState(null);
  const stable = useMemo(() => items || [], [items]);

  const open = useCallback((src) => {
    const idx = stable.findIndex((it) => it.src === src);
    setOpenIndex(idx >= 0 ? idx : 0);
  }, [stable]);

  const value = useMemo(() => ({ items: stable, open }), [stable, open]);

  return (
    <GalleryContext.Provider value={value}>
      {children}
      {openIndex !== null && (
        <ScreenshotLightbox
          items={stable}
          index={openIndex}
          onClose={() => setOpenIndex(null)}
          onNavigate={(i) => setOpenIndex(i)}
        />
      )}
    </GalleryContext.Provider>
  );
}

// Reusable screenshot block. Either pass `frame="hero"` for the prominent hero
// figure, or default for inline / grid screenshots. Clicking opens the
// surrounding <ScreenshotGallery> in lightbox. If used standalone (no gallery),
// it manages its own lightbox state.
export default function LandingFigure({
  src,
  alt,
  caption,
  width = 1440,
  height = 900,
  eager = false,
  ring = 'ring-amber-500/40',
  frame = 'default',
  className = '',
  imgClassName = '',
  showExpandHint = true,
}) {
  const { t } = useTranslation('common');
  const gallery = useContext(GalleryContext);
  const [standaloneOpen, setStandaloneOpen] = useState(false);
  const figureRef = useRef(null);

  const handleClick = (e) => {
    e.preventDefault();
    if (gallery) gallery.open(src);
    else setStandaloneOpen(true);
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick(e);
    }
  };

  const wrapperClass = frame === 'hero'
    ? `relative rounded-xl overflow-hidden border border-gray-800 bg-[#111113] shadow-[0_30px_80px_-20px_rgba(245,158,11,0.18)] ring-1 ${ring}`
    : 'relative rounded-xl overflow-hidden border border-gray-800 bg-[#111113]';

  return (
    <>
      <figure ref={figureRef} className={className}>
        <button
          type="button"
          onClick={handleClick}
          onKeyDown={handleKey}
          aria-label={alt ? t('common.open_screenshot_named', { alt }) : t('common.open_screenshot')}
          className={`group block w-full ${wrapperClass} cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0a0b] transition-shadow hover:shadow-[0_30px_80px_-20px_rgba(245,158,11,0.3)]`}
        >
          <img
            src={src}
            alt={alt || ''}
            width={width}
            height={height}
            fetchPriority={eager ? 'high' : undefined}
            loading={eager ? 'eager' : 'lazy'}
            className={`w-full h-auto ${imgClassName}`}
            draggable={false}
          />
          {showExpandHint && (
            <span className="pointer-events-none absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-black/70 text-white text-[11px] font-medium opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity backdrop-blur-sm border border-white/10">
              <Expand className="w-3 h-3" />
              <span>{t('common.zoom')}</span>
            </span>
          )}
        </button>
        {caption && frame !== 'hero' && (
          <figcaption className="px-5 py-3 text-sm text-gray-400 border-t border-gray-800">{caption}</figcaption>
        )}
      </figure>
      {caption && frame === 'hero' && (
        <p className="text-xs text-gray-500 mt-3 px-1">{caption}</p>
      )}
      {!gallery && standaloneOpen && (
        <ScreenshotLightbox
          items={[{ src, alt, caption }]}
          index={0}
          onClose={() => setStandaloneOpen(false)}
          onNavigate={() => {}}
        />
      )}
    </>
  );
}
